import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, ScrollText, Trophy } from "lucide-react";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import { getArchiveWall } from "../lib/auth";
import { RARITY_STYLES } from "../lib/archive";
import { useAuth } from "../lib/auth";

// Rarity border palette — bronze / silver / blue / gold
const RARITY_BORDER = {
  C: "#8C5A2B", // bronze
  B: "#B8B8B8", // silver
  A: "#3F7BD0", // blue
  S: "#C5A059", // gold
};

export default function ArchiveWallPage() {
  const { user } = useAuth();
  const [tiles, setTiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getArchiveWall();
        setTiles(d.tiles || []);
        setTotal(d.total || 0);
      } catch { /* ignore */ }
      setLoaded(true);
    })();
  }, []);

  return (
    <div data-testid="archive-wall-page">
      <Section testId="wall-hero" className="pt-12 md:pt-24">
        <Eyebrow>The Bakhtak Archive — Public Wall</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-4xl">
          Records the world has{" "}
          <span className="text-[#C5A059] italic font-light">already recovered</span>.
        </h1>
        <Lede>
          Every face here was unsealed by someone, somewhere. Names, nations, and lore
          remain classified — study the artwork carefully. Patterns reveal themselves
          to those who look long enough.
        </Lede>

        <div className="mt-8 flex flex-wrap items-baseline gap-4">
          <div className="font-display text-4xl md:text-5xl text-[#C5A059] tabular-nums" data-testid="wall-total-count">
            {total}
          </div>
          <div className="text-xs tracking-[0.28em] uppercase text-[#A39E99]">
            records on the wall
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to={user ? "/archive/drop" : "/login"}
            data-testid="wall-cta-play"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#D4AF68]"
          >
            <Trophy size={14} /> {user ? "Crack the Current Signal" : "Enter The Archive — Sign In"}
            <ArrowRight size={14} />
          </Link>
          {user && (
            <Link
              to="/me/cast"
              data-testid="wall-cta-my-cast"
              className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-6 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#C5A059]/10"
            >
              <ScrollText size={14} /> My Cast
            </Link>
          )}
        </div>
      </Section>

      <Section testId="wall-grid" className="pt-0 pb-24">
        {!loaded ? (
          <p className="text-sm text-[#737373]">Opening the vault…</p>
        ) : tiles.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-10 md:p-16 text-center" data-testid="wall-empty">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">The Wall Is Empty</div>
            <p className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-3">
              No records have surfaced yet.
            </p>
            <p className="text-sm text-[#A39E99] mt-3 max-w-md mx-auto">
              Be the first to crack the current signal and add your record to the wall.
            </p>
            <Link
              to={user ? "/archive/drop" : "/login"}
              className="mt-6 inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#D4AF68]"
              data-testid="wall-empty-cta"
            >
              {user ? "Open the Signal" : "Sign In"} <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-testid="wall-grid-list">
            {tiles.map((t, idx) => {
              const border = RARITY_BORDER[t.rarity] || RARITY_BORDER.C;
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(0.4, idx * 0.02) }}
                  className="relative aspect-[3/4] overflow-hidden border-2 bg-[#050505]"
                  style={{ borderColor: `${border}66`, boxShadow: `0 0 30px -16px ${border}` }}
                  data-testid="wall-tile"
                >
                  {t.image ? (
                    <img
                      src={t.image}
                      alt="Recovered record"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: t.image_position || "center" }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#444] text-xs">
                      Artwork
                    </div>
                  )}
                  {/* No name. No nation. No rarity label. Border-color hints at rarity. */}
                </motion.div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
